import { useState, useEffect, useMemo } from 'react';
import { X, Play, Loader2, FileText, MessageSquarePlus, CheckCircle2, Upload, Crown, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Task, AVAILABLE_TASKS, TASK_CATEGORY_INFO, TASK_TO_CATEGORY } from '@/types/taskTypes';
import { documentApi, SessionDocument, taskApi, patentApi, userApi } from '@/services/api';
import { OA_EMAIL_TEMPLATES } from '@/config/emailTemplates';
import { useNotificationStore, useSessionStore } from '@/stores';
import type { TaskResultAttachment } from '@/stores/sessionStore';
import { useAuth } from '@/context/AuthContext';
import { EmailTemplateManager } from '@/components/EmailTemplateManager';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PartsListDisplay from './task-displays/PartsListDisplay';
import SpecMapDisplay from './task-displays/SpecMapDisplay';
import ClaimsSupportDisplay from './task-displays/ClaimsSupportDisplay';
import ClaimsWithNumeralsDisplay from './task-displays/ClaimsWithNumeralsDisplay';
import CustomAnalysisDisplay from './task-displays/CustomAnalysisDisplay';
import IRMetadataDisplay from './task-displays/IRMetadataDisplay';
import IRInnovationAnalysisDisplay from './task-displays/IRInnovationAnalysisDisplay';
import IRTechClassificationDisplay from './task-displays/IRTechClassificationDisplay';
import DraftSummaryDisplay from './task-displays/DraftSummaryDisplay';
import DraftDetailedDescriptionDisplay from './task-displays/DraftDetailedDescriptionDisplay';
import DraftBackgroundDisplay from './task-displays/DraftBackgroundDisplay';
import DraftClaimsDisplay from './task-displays/DraftClaimsDisplay';
import OtherEmbodimentsDisplay from './task-displays/OtherEmbodimentsDisplay';
import OAExtractDisplay from './task-displays/OAExtractDisplay';
import OAAnalyzeDisplay from './task-displays/OAAnalyzeDisplay';
import OASummaryArtDisplay from './task-displays/OASummaryArtDisplay';
import OATimelineDisplay from './task-displays/OATimelineDisplay';
import TranslateDocumentDisplay from './task-displays/TranslateDocumentDisplay';
import LaTeXConvertDisplay from './task-displays/LaTeXConvertDisplay';
import OCRPdfDisplay from './task-displays/OCRPdfDisplay';
import AntecedentBasisDisplay from './task-displays/AntecedentBasisDisplay';
import TechnicalOutlineDisplay from './task-displays/TechnicalOutlineDisplay';
import PartsProcessOutlineDisplay from './task-displays/PartsProcessOutlineDisplay';

interface UniversalTaskPanelProps {
  task: Task;
  onClose: () => void;
}

// Language options for translation tasks
const LANGUAGE_OPTIONS = [
  { value: 'auto', label: 'Auto-detect' },
  { value: 'en', label: 'English' },
  { value: 'zh', label: 'Chinese (Simplified)' },
  { value: 'zh-TW', label: 'Chinese (Traditional)' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'it', label: 'Italian' },
  { value: 'ru', label: 'Russian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' },
  { value: 'nl', label: 'Dutch' },
  { value: 'sv', label: 'Swedish' },
  { value: 'da', label: 'Danish' },
  { value: 'no', label: 'Norwegian' },
  { value: 'fi', label: 'Finnish' },
  { value: 'pl', label: 'Polish' }
];

export function UniversalTaskPanel({ task, onClose }: UniversalTaskPanelProps) {
  const [textInput, setTextInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  // Dynamic form fields
  const [sourceLanguage, setSourceLanguage] = useState('auto');
  const [targetLanguage, setTargetLanguage] = useState('en');
  const [patentNumber, setPatentNumber] = useState('');
  const [applicationNumber, setApplicationNumber] = useState('');
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [selectedResourceType, setSelectedResourceType] = useState('');
  const [useEntireDescription, setUseEntireDescription] = useState(false);
  const [useEntireClaims, setUseEntireClaims] = useState(false);

  // Office Action task-specific fields
  const [selectedApplication, setSelectedApplication] = useState('');
  const [sessionApplications, setSessionApplications] = useState<any[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [selectedOfficeAction, setSelectedOfficeAction] = useState('');
  const [prosecutionDocs, setProsecutionDocs] = useState<any[]>([]);
  const [prosecutionDocsLoading, setProsecutionDocsLoading] = useState(false);

  // OA Reporting Email template selection
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('standard');
  const [userEmailTemplates, setUserEmailTemplates] = useState<any[]>([]);
  const [allEmailTemplates, setAllEmailTemplates] = useState(OA_EMAIL_TEMPLATES);
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  // Task history and tabs
  const [activeTab, setActiveTab] = useState<'result' | 'history'>('result');
  const [taskHistory, setTaskHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // File upload for LaTeX conversion and OCR
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [outputFilename, setOutputFilename] = useState('');
  const [outputFormat, setOutputFormat] = useState<'txt' | 'json' | 'docx'>('txt');
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<any>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Translation task input mode
  const [translationInputMode, setTranslationInputMode] = useState<'onboarded' | 'upload' | 'text' | null>(null);

  // Antecedent basis check input mode
  const [antecedentInputMode, setAntecedentInputMode] = useState<'onboarded' | 'raw_text' | 'upload' | null>(null);

  // Technical outline input mode
  const [technicalOutlineInputMode, setTechnicalOutlineInputMode] = useState<'raw_text' | 'upload' | null>(null);

  // Parts process outline input mode
  const [partsProcessOutlineInputMode, setPartsProcessOutlineInputMode] = useState<'raw_text' | 'upload' | null>(null);

  // Document search for translation task
  const [documentSearchQuery, setDocumentSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SessionDocument[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Fetch documents from API
  const [sessionDocuments, setSessionDocuments] = useState<SessionDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);

  // Notification store
  const { addNotification } = useNotificationStore();
  const addTaskAttachment = useSessionStore(state => state.addTaskAttachment);

  // Account info for premium checking
  const { user } = useAuth();
  const [accountInfo, setAccountInfo] = useState<any>(null);

  // Fetch documents when panel opens
  useEffect(() => {
    const loadDocuments = async () => {
      setDocumentsLoading(true);
      try {
        const documents = await documentApi.fetchSessionDocuments(100);
        setSessionDocuments(documents);
      } catch (error) {
        console.error('Failed to load documents:', error);
      } finally {
        setDocumentsLoading(false);
      }
    };

    loadDocuments();
  }, []);

  // Fetch account info when panel opens
  useEffect(() => {
    const fetchAccountInfo = async () => {
      if (!user) return;
      try {
        const response = await userApi.fetchAccountInfo();
        if (response && !response.error) {
          setAccountInfo(response);
        }
      } catch (error) {
        console.error('Failed to fetch account info:', error);
      }
    };
    fetchAccountInfo();
  }, [user]);

  // Clear result and form when task changes
  useEffect(() => {
    setResult(null);
    setError('');
    setTextInput('');
    setSelectedResourceId('');
    setSelectedResourceType('');
    setUploadedFile(null);
    setActiveTab('result');
    setTranslationInputMode(null);
    setAntecedentInputMode(null);
    setTechnicalOutlineInputMode(null);
    setPartsProcessOutlineInputMode(null);
  }, [task.id]);

  // Debounced document search
  useEffect(() => {
    const searchDocuments = async () => {
      if (!documentSearchQuery.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      if (documentSearchQuery.trim().length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      console.log('[Document Search] Searching for:', documentSearchQuery);
      setIsSearching(true);
      try {
        const results = await documentApi.searchDocuments(documentSearchQuery, 50);
        console.log('[Document Search] Results:', results);
        setSearchResults(results);
      } catch (error) {
        console.error('[Document Search] Failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchDocuments, 1000);
    return () => clearTimeout(timeoutId);
  }, [documentSearchQuery]);

  // Auto-select first search result when results come back
  useEffect(() => {
    if (searchResults.length > 0 && documentSearchQuery.trim()) {
      const firstResult = searchResults[0];
      setSelectedResourceId(firstResult.resource_id);
      setSelectedResourceType(firstResult.file_type);
      console.log('[Document Search] Auto-selected first result:', firstResult.resource_id);
    }
  }, [searchResults, documentSearchQuery]);

  // Function to load user email templates
  const loadUserTemplates = async () => {
    try {
      const response = await userApi.fetchEmailTemplates();
      const userTemplates = response.templates || [];
      setUserEmailTemplates(userTemplates);

      // Merge default and user templates
      const userTemplatesFormatted = userTemplates.map((t: any) => ({
        id: t.prompt_id,
        name: t.name,
        description: t.description || 'Custom template',
        structure: t.template_structure,
      }));

      setAllEmailTemplates([...OA_EMAIL_TEMPLATES, ...userTemplatesFormatted]);
    } catch (error) {
      console.error('Failed to load user email templates:', error);
      // On error, just use default templates
      setAllEmailTemplates(OA_EMAIL_TEMPLATES);
    }
  };

  // Load user email templates on mount
  useEffect(() => {
    loadUserTemplates();
  }, []);

  // Reusable function to load task history
  const loadTaskHistory = async () => {
    setHistoryLoading(true);
    try {
      // Use task.id as the filter to get specific task history
      const response = await taskApi.getTaskHistory(task.id);

      const allHistory = (response as any).history || [];

      // Filter by task ID specifically
      const filteredHistory = allHistory.filter((item: any) => {
        return item.task_type === task.id;
      });

      // Sort by newest first
      const sortedHistory = filteredHistory.sort((a: any, b: any) => {
        const dateA = new Date(a.time_received || a.timestamp).getTime();
        const dateB = new Date(b.time_received || b.timestamp).getTime();
        return dateB - dateA;
      });

      setTaskHistory(sortedHistory);
    } catch (error) {
      console.error('Failed to load task history:', error);
      setTaskHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetch task history when panel opens or task changes
  useEffect(() => {
    // Clear selected history item when task changes
    setSelectedHistoryItem(null);
    // Reset translation input mode when task changes
    setTranslationInputMode(null);
    loadTaskHistory();
  }, [task.id]);

  // Fetch session applications for OA tasks
  useEffect(() => {
    const isOATask = task.id.includes('oa_');
    if (!isOATask) return;

    const loadApplications = async () => {
      setApplicationsLoading(true);
      try {
        const apps = await patentApi.getSessionApplications();

        // Get active applications from session context
        const sessionContextStr = localStorage.getItem('session_context');
        let activeAppIds: string[] = [];
        if (sessionContextStr && sessionContextStr !== 'undefined') {
          try {
            const sessionContext = JSON.parse(sessionContextStr);
            activeAppIds = sessionContext.current_applications || [];
          } catch (e) {
            console.error('Error parsing session context:', e);
          }
        }

        // Sort applications: active ones first, then by filing date
        const sortedApps = apps.sort((a, b) => {
          const aActive = activeAppIds.includes(a.application_id);
          const bActive = activeAppIds.includes(b.application_id);

          if (aActive !== bActive) {
            return aActive ? -1 : 1;
          }

          // Then sort by filing date (newest first)
          const aDate = a.filing_date ? new Date(a.filing_date).getTime() : 0;
          const bDate = b.filing_date ? new Date(b.filing_date).getTime() : 0;
          return bDate - aDate;
        });

        setSessionApplications(sortedApps.map(app => ({
          ...app,
          isActive: activeAppIds.includes(app.application_id)
        })));
      } catch (error) {
        console.error('Failed to load applications:', error);
      } finally {
        setApplicationsLoading(false);
      }
    };

    loadApplications();
  }, [task.id]);

  // Fetch prosecution documents when application is selected for OA tasks
  useEffect(() => {
    const isOATask = task.id.includes('oa_');
    if (!isOATask || !selectedApplication) return;

    const loadProsecutionDocs = async () => {
      setProsecutionDocsLoading(true);
      try {
        const result = await patentApi.fetchProsecutionDocInfo(selectedApplication);
        console.log('Prosecution doc info result:', result);

        // Handle different response formats
        let prosInfoArray: any[] = [];
        if (result.pros_info && Array.isArray(result.pros_info)) {
          prosInfoArray = result.pros_info;
        } else if (Array.isArray(result)) {
          prosInfoArray = result;
        }

        // Map prosecution documents
        const docs = prosInfoArray
          .filter(doc => Array.isArray(doc.downloadOptionBag))
          .map(doc => {
            const pdf = doc.downloadOptionBag.find((d: any) => d.mimeTypeIdentifier === 'PDF');
            return {
              date: new Date(doc.officialDate).toLocaleDateString(),
              rawDate: new Date(doc.officialDate),
              pdfUrl: pdf?.downloadUrl || null,
              description: doc.documentCodeDescriptionText || doc.documentCode,
              documentCode: doc.documentCode,
              docId: doc.documentIdentifier
            };
          })
          .filter(doc => !!doc.pdfUrl)
          .sort((a, b) => b.rawDate.getTime() - a.rawDate.getTime());

        // Filter to only office action documents (including restrictions)
        const oaDocs = docs.filter(doc => {
          const desc = doc.description.toLowerCase();
          const code = doc.documentCode;
          // Include office actions, rejections, allowances, notifications, and restriction requirements
          return /(office action|rejection|allowance|notification)/i.test(desc) || code === 'CTRS';
        });

        setProsecutionDocs(oaDocs);
      } catch (error) {
        console.error('Failed to load prosecution documents:', error);
        setProsecutionDocs([]);
      } finally {
        setProsecutionDocsLoading(false);
      }
    };

    loadProsecutionDocs();
  }, [task.id, selectedApplication]);

  // Helper function to format timestamps correctly in local time
  const formatTimestamp = (timestamp: string | Date | undefined): string => {
    if (!timestamp) return 'Unknown time';

    try {
      let dateStr = timestamp;

      // If timestamp is a string, check if it has a timezone indicator
      if (typeof dateStr === 'string') {
        // Check if timestamp has timezone indicator (Z, +HH:MM, or -HH:MM)
        const hasTimezone = /[Z]$|[+-]\d{2}:\d{2}$/.test(dateStr);

        // If no timezone indicator, assume UTC and add 'Z'
        if (!hasTimezone && dateStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
          // Remove any decimal seconds first
          const baseTimestamp = dateStr.split('.')[0];
          dateStr = baseTimestamp + 'Z';
        }
        // If timestamp has microseconds, truncate to milliseconds
        else if (dateStr.includes('.')) {
          const parts = dateStr.split('.');
          if (parts.length === 2) {
            const fractionalPart = parts[1];
            // Check if there's more than 3 digits before timezone indicator
            const timezoneMatch = fractionalPart.match(/([+-]\d{2}:\d{2}|Z)$/);
            if (timezoneMatch) {
              const timezone = timezoneMatch[0];
              const digits = fractionalPart.substring(0, fractionalPart.indexOf(timezone));
              if (digits.length > 3) {
                // Truncate to 3 digits (milliseconds)
                dateStr = parts[0] + '.' + digits.substring(0, 3) + timezone;
              }
            }
          }
        }
      }

      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting timestamp:', timestamp, error);
      return String(timestamp);
    }
  };

  // Get category info
  const categoryKey = TASK_TO_CATEGORY[task.id] || 'legacy';
  const categoryInfo = TASK_CATEGORY_INFO[categoryKey];
  const CategoryIcon = categoryInfo?.icon;

  // Extract result data from result object, checking multiple possible properties
  const getResultData = (resultObj: any) => {
    if (!resultObj) return null;

    // Check multiple possible property names where the actual data might be stored
    // Priority: result > output > analysis > use the object itself
    if (resultObj.result !== undefined) return resultObj.result;
    if (resultObj.output !== undefined) return resultObj.output;
    if (resultObj.analysis !== undefined) return resultObj.analysis;

    // If none of the above exist, assume the object itself is the data
    return resultObj;
  };

  // Parse result output - it comes as a string that may need parsing
  const getParsedResult = (output: any) => {
    if (!output) return null;

    // If it's already an object, return it
    if (typeof output === 'object') return output;

    // If it's a string, try to parse it as JSON
    if (typeof output === 'string') {
      // Check if it looks like a Python dictionary (starts with { and contains single quotes)
      if (!output.trim().startsWith('{') || !output.includes("'")) {
        return output;
      }

      try {
        // Try to parse as JSON first
        return JSON.parse(output);
      } catch {
        // If parsing fails, try to evaluate as Python dict string
        try {
          // More robust Python to JSON conversion
          // Handle single quotes more carefully to avoid breaking contractions
          let jsonStr = output;

          // Replace single quotes only in structural positions (keys, values, arrays)
          jsonStr = jsonStr.replace(/'([^']*)':/g, '"$1":');  // Keys
          jsonStr = jsonStr.replace(/:\s*'([^']*)'/g, ': "$1"');  // Values after colons
          jsonStr = jsonStr.replace(/\[\s*'([^']*)'/g, '["$1"');  // Array first item
          jsonStr = jsonStr.replace(/,\s*'([^']*)'/g, ', "$1"');  // Array other items
          jsonStr = jsonStr.replace(/'([^']*)']/g, '"$1"]');  // Array last item

          // Handle Python boolean and null values
          jsonStr = jsonStr.replace(/True/g, 'true');
          jsonStr = jsonStr.replace(/False/g, 'false');
          jsonStr = jsonStr.replace(/None/g, 'null');

          return JSON.parse(jsonStr);
        } catch (e) {
          console.warn('Failed to parse Python dict:', output.substring(0, 100) + '...', 'Error:', e);
          // If all parsing fails, return the string as-is
          return output;
        }
      }
    }

    return output;
  };

  const getDocumentIdFromResult = (taskResult: any, fallbackId?: string): string | undefined => {
    if (!taskResult) return fallbackId;

    const candidateFields = [
      'document_id',
      'doc_id',
      'docId',
      'documentId',
      'doc_no',
      'docNo',
      'selected_number',
      'selectedNumber',
      'selected_doc_id',
      'selectedDocId',
      'selected_document',
      'selectedDocument',
      'document',
      'doc',
      'application_number',
      'applicationNumber',
      'resource_id',
      'resourceId',
    ];

    const extractFrom = (source: any): string | undefined => {
      if (!source) return undefined;
      for (const field of candidateFields) {
        const value = source[field];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
        if (typeof value === 'number') {
          return value.toString();
        }
      }
      return undefined;
    };

    return extractFrom(taskResult) ||
           extractFrom(taskResult?.metadata) ||
           (fallbackId ? fallbackId.trim() : undefined);
  };

  const normalizeResourceId = (resourceId?: string | null) => {
    if (!resourceId) return undefined;
    const parts = resourceId.split('-');
    const withoutType = parts.length > 1 ? parts.slice(1).join('-') : resourceId;
    return withoutType || resourceId;
  };

  // Helper to get active document IDs with their types from localStorage
  const getActiveDocuments = (): Map<string, string> => {
    try {
      const sessionContextStr = localStorage.getItem('session_context');
      if (!sessionContextStr || sessionContextStr === 'undefined') return new Map();

      const sessionContext = JSON.parse(sessionContextStr);
      const activeDocuments = new Map<string, string>();

      // Map session_context keys to document types
      const typeMapping: Record<string, string> = {
        'current_patents': 'patent',
        'current_publications': 'publication',
        'current_applications': 'application',
        'pros_docs': 'application', // prosecution docs are usually applications
        'resource_ids': 'upload'
      };

      // Collect all active document IDs with their types
      Object.entries(typeMapping).forEach(([key, docType]) => {
        if (sessionContext[key] && Array.isArray(sessionContext[key])) {
          sessionContext[key].forEach((id: string) => {
            activeDocuments.set(id, docType);
          });
        }
      });

      return activeDocuments;
    } catch (error) {
      console.error('Error parsing session_context:', error);
      return new Map();
    }
  };

  // Determine if task is office action related
  const isOfficeActionTask = () => {
    return task.id.includes('oa_') || task.id.includes('form_');
  };

  // Filter and sort documents based on task type
  const availableDocuments = useMemo(() => {
    const activeDocs = getActiveDocuments();
    let allDocs: SessionDocument[] = [];

    if (isOfficeActionTask()) {
      // Office action tasks only show applications
      allDocs = sessionDocuments.filter(doc => doc.file_type === 'application');
    } else {
      // Spec-related tasks show patents, publications, uploads, and foreign
      const allowedTypes = ['patent', 'publication', 'upload', 'foreign'];
      allDocs = sessionDocuments.filter(doc => allowedTypes.includes(doc.file_type));
    }

    // Add isActive flag and sort: active documents first, then by name
    const docsWithActive = allDocs.map(doc => {
      // Extract the ID part (e.g., 'publication-20190190617' -> '20190190617')
      const idParts = doc.resource_id.split('-');
      const docId = idParts.length > 1 ? idParts.slice(1).join('-') : doc.resource_id;

      // Check if this document is active by matching both ID and type
      const isActive = activeDocs.has(docId) && activeDocs.get(docId) === doc.file_type;
      return {
        ...doc,
        isActive
      };
    });

    return docsWithActive.sort((a, b) => {
      if (a.isActive !== b.isActive) {
        return a.isActive ? -1 : 1;
      }
      const nameA = a.file_name || a.resource_id || '';
      const nameB = b.file_name || b.resource_id || '';
      return nameA.localeCompare(nameB);
    });
  }, [sessionDocuments, task.id]);

  // Determine input requirements based on task type
  const requiresDocumentSelection = () => {
    // Exclude translate_document, antecedent_basis_check, technical_outline, and parts_process_outline as they have their own custom input UI
    if (task.id === 'translate_document' || task.id === 'antecedent_basis_check' || task.id === 'technical_outline' || task.id === 'parts_process_outline') return false;
    // Check if task has resource_interface that requires resource_id
    return task.resource_interface?.input?.resource_id === true;
  };

  const hasClaimsSupportOptions = () => {
    // Check if task supports description/claims checkboxes
    return task.resource_interface?.input?.use_entire_description === true ||
           task.resource_interface?.input?.use_entire_claims === true;
  };

  const requiresTextInput = () => {
    // Custom analysis and draft tasks need both document selection AND text input
    if (task.id === 'custom_analysis' || task.id.includes('draft_')) {
      return true;
    }
    // If document selection is required, text input is optional
    // Otherwise, text input is required
    return !requiresDocumentSelection();
  };

  const requiresPatentSelection = () => {
    // Tasks that optionally support patent selection (plain text input)
    return task.id.includes('form_');
  };

  const requiresApplicationNumber = () => {
    return task.id.includes('form_');
  };

  const isTranslationTask = () => {
    return task.id.startsWith('translate_');
  };

  const requiresOfficeActionDocument = () => {
    // OA Timeline only needs application, not a specific OA document
    return task.id.includes('oa_') && task.id !== 'oa_timeline';
  };

  // Handle adding task result to chat
  const handleAddToChat = (taskResult: any, taskName: string, taskType: string, timestamp: string) => {
    // Extract and format the content
    const resultData = getParsedResult(getResultData(taskResult));
    const contentString = typeof resultData === 'string'
      ? resultData
      : JSON.stringify(resultData, null, 2);

    const fallbackDocId = taskResult === result ? normalizeResourceId(selectedResourceId) : undefined;
    const documentId = getDocumentIdFromResult(taskResult, fallbackDocId);

    // Create task attachment
    const attachment: TaskResultAttachment = {
      id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      taskName: taskName,
      taskType: taskType,
      timestamp: timestamp,
      content: contentString,
      documentId,
    };

    // Add to global store
    addTaskAttachment(attachment);

    // Show notification
    addNotification({
      type: 'success',
      title: 'Added to Chat',
      message: `${taskName} result has been added to your chat input.`,
      duration: 3000,
    });
  };

  const getInputPlaceholder = () => {
    if (task.id.includes('ir_')) {
      return 'Paste your invention record text here...';
    } else if (task.id.includes('draft_')) {
      return 'Enter your invention disclosure or technical content here...';
    } else if (task.id.includes('oa_')) {
      return 'Paste the office action text here...';
    } else if (task.id.includes('form_')) {
      return 'Enter the required information here...';
    } else if (isTranslationTask()) {
      return 'Enter the text you want to translate...';
    } else if (task.id === 'parts_list') {
      return 'The parts list will be generated from the selected patent document...';
    } else if (task.id === 'spec_map') {
      return 'The specification map will be generated from the selected patent document...';
    } else if (task.id === 'claims_support') {
      return 'Claims support analysis will be performed on the selected patent document...';
    } else if (task.id === 'claims_with_numerals') {
      return 'Reference numerals will be added to claims from the selected patent document...';
    } else if (task.id === 'custom_analysis') {
      return 'Describe what analysis or information you need (e.g., "Compare the claims to identify overlapping scope" or "Summarize the key technical advantages")';
    }
    return 'Enter your text content here...';
  };

  const getTaskInstructions = () => {
    // Document Analysis Tasks
    if (task.id === 'parts_list') {
      return 'Automatically extract and organize all components, parts, and elements from the patent specification. The generated list includes reference numerals and detailed descriptions, making it easier to track components across your document.';
    } else if (task.id === 'spec_map') {
      return 'Create a comprehensive map linking specification details to parts, features, and reference numerals. This helps ensure consistency between your drawings, detailed description, and claims.';
    } else if (task.id === 'antecedent_basis_check') {
      return 'Analyze each claim to verify that all elements have proper antecedent basis. The tool checks that claim terms are properly introduced before being referenced, identifies missing antecedents, and ensures clear referential relationships throughout your claim set.';
    } else if (task.id === 'technical_outline') {
      return 'Generate a comprehensive technical outline from your invention disclosure for patent specification drafting. The outline covers technical field, background, summary, detailed description (components, processes, features), alternative embodiments, applications, implementation details, and claims considerations. Provide a detailed disclosure for best results.';
    } else if (task.id === 'parts_process_outline') {
      return 'Generate a detailed parts hierarchy and process outline from your invention disclosure. This task focuses on granular technical details, identifying specific parts with their technical language, hierarchical relationships (parts to subparts), functional relationships between components, process steps with dependencies, and system flows. Ideal for detailed patent specification drafting where precise component relationships and process flows need to be mapped.';
    } else if (task.id === 'claims_support') {
      return 'Verify that each claim element is properly supported by the specification. The analysis identifies which portions of the specification support each claim limitation, helping you strengthen your claims and prepare for prosecution.';
    } else if (task.id === 'claims_with_numerals') {
      return 'Automatically insert reference numerals from your specification into claim language. This enhances claim clarity by linking claim elements to specific components shown in the drawings and described in the detailed description.';
    } else if (task.id === 'custom_analysis') {
      return 'Run specialized analysis on your patent documents using custom prompts. Define your own analysis criteria to extract specific information, check for particular issues, or generate custom reports tailored to your needs.';
    } else if (task.id === 'technical_outline') {
      return 'Generate a comprehensive technical outline from your invention disclosure to guide patent specification drafting. The outline organizes your disclosure into structured subsections with specific content points, word targets, and technical depth levels, providing a roadmap for creating a complete and well-organized patent specification.';

    // Invention Record Tasks
    } else if (task.id.includes('ir_metadata')) {
      return 'Extract metadata including inventors, assignees, filing dates, and technical classifications from your invention record. This structured data can be used to populate forms and track invention details.';
    } else if (task.id.includes('ir_innovation')) {
      return 'Analyze the innovation aspects and technological advancements described in your invention record. Identify key innovations, technical improvements, and competitive advantages that should be highlighted in the patent application.';
    } else if (task.id.includes('ir_tech_class')) {
      return 'Classify your invention into appropriate technology categories and patent classifications (CPC/IPC). Proper classification helps with prior art searching and ensures your application is examined by specialists in the relevant field.';

    // Patent Drafting Tasks
    } else if (task.id === 'draft_detailed_description') {
      return 'Generate a comprehensive detailed description section for your patent application. The system analyzes your input and creates a thorough technical description with proper structure, terminology, and reference numerals.';
    } else if (task.id === 'draft_other_embodiments') {
      return 'Generate alternative embodiments and variations to broaden patent scope. The tool suggests different implementations, configurations, and use cases that can strengthen your patent protection.';
    } else if (task.id === 'draft_claims') {
      return 'Generate patent claims with proper formatting and structure from your disclosure. The system creates independent and dependent claims following USPTO requirements, with appropriate antecedent basis and claim differentiation.';
    } else if (task.id === 'draft_background') {
      return 'Generate a background section describing the field and existing technology. The analysis identifies the technical problem, discusses limitations of prior solutions, and sets up the context for your invention.';
    } else if (task.id === 'draft_summary') {
      return 'Generate a concise summary section for your patent application. The summary highlights key aspects of the invention and provides a high-level overview suitable for patent abstracts and summaries.';

    // Office Action Tasks
    } else if (task.id.includes('oa_extract')) {
      return 'Extract key information and requirements from the office action document. The analysis identifies rejections, objections, deadlines, cited prior art, and specific examiner requirements to help you plan your response strategy.';
    } else if (task.id.includes('oa_analyze')) {
      return 'Analyze office action issues and provide detailed assessment of rejections. The tool examines each rejection, evaluates its strength, and suggests potential response strategies based on the cited prior art and examiner\'s reasoning.';
    } else if (task.id.includes('oa_summary_art')) {
      return 'Summarize the prior art references cited in the office action. Get clear, concise summaries of each cited document including their key teachings, relevant figures, and how the examiner applied them against your claims.';
    } else if (task.id.includes('oa_reporting_email')) {
      return 'Generate a professional email report summarizing the office action for client communication. Choose from various templates to create clear, client-friendly explanations of office action issues, deadlines, and recommended next steps.';
    } else if (task.id.includes('oa_timeline')) {
      return 'Create a timeline of prosecution history from the application documents. Track all office actions, responses, amendments, and key events to understand the prosecution history and identify patterns or issues.';

    // Translation & Document Processing
    } else if (isTranslationTask()) {
      return 'Translate patent documents while preserving technical accuracy and legal terminology. The translation maintains proper claim structure, reference numerals, and technical terms to ensure the translated document meets patent office requirements.';
    } else if (task.id === 'latex_to_docx') {
      return 'Convert LaTeX documents (including ZIP archives with multiple files) to Microsoft Word format. Mathematical equations, special characters, and formatting are preserved during conversion, making it easier to edit and finalize your documents.';
    } else if (task.id === 'ocr_pdf') {
      return 'Extract text from scanned PDF documents using advanced OCR technology. Choose between text, Word, or JSON output formats. JSON output includes positional data for advanced processing and analysis.';
    }

    return task.description;
  };

  // Get display name for a document (shows filename for uploads, ID for others)
  const getDocumentDisplayName = (documentId: string, resourceType: string) => {
    if (resourceType === 'upload') {
      // For uploads, look up the filename from availableDocuments
      const doc = availableDocuments.find(d => d.resource_id === documentId || d.resource_id === `upload-${documentId}`);
      return doc?.file_name || documentId;
    }
    // For patents/publications/applications, show the ID as-is
    return documentId;
  };

  const handleRunTask = async () => {
    // Check if task is premium-only and user is not premium
    if (task.premium_only && accountInfo?.account_type !== 'premium') {
      addNotification({
        type: 'warning',
        title: 'Premium Feature Required',
        message: `${task.name} is only available for Premium subscribers. Please upgrade to access this feature.`,
        duration: 5000,
      });
      return;
    }

    // Validation for Office Action tasks (only oa_ tasks, not form_ tasks)
    if (task.id.includes('oa_')) {
      if (!selectedApplication) {
        setError('Please select an application');
        return;
      }
      if (requiresOfficeActionDocument() && !selectedOfficeAction) {
        setError('Please select an office action document');
        return;
      }
    }
    // Validation for other document selection tasks
    else if (requiresDocumentSelection()) {
      // For custom_analysis and draft tasks, document selection is optional but text input is required
      if (task.id === 'custom_analysis' || task.id.includes('draft_')) {
        if (!textInput.trim()) {
          setError('Please enter text content');
          return;
        }
      } else {
        // For other tasks (parts_list, spec_map, etc.), document selection is required
        if (!selectedResourceId) {
          setError('Please select a document to process');
          return;
        }
      }
    }
    // Skip validation for file upload tasks (latex_to_docx, ocr_pdf, translate_document with file, antecedent_basis_check, technical_outline, parts_process_outline)
    else if (task.id !== 'latex_to_docx' && task.id !== 'ocr_pdf' && task.id !== 'antecedent_basis_check' && task.id !== 'technical_outline' && task.id !== 'parts_process_outline' && !(task.id === 'translate_document' && uploadedFile) && !textInput.trim() && !patentNumber.trim()) {
      setError('Please enter text content or select a patent document');
      return;
    }

    // Validation for translation tasks
    if (task.id === 'translate_document') {
      if (!translationInputMode) {
        setError('Please select an input method (onboarded document, upload, or raw text)');
        return;
      }
      if (translationInputMode === 'onboarded' && !selectedResourceId) {
        setError('Please select a document to translate');
        return;
      }
      if (translationInputMode === 'upload' && !uploadedFile) {
        setError('Please upload a document to translate');
        return;
      }
      if (translationInputMode === 'text' && !textInput.trim()) {
        setError('Please enter text to translate');
        return;
      }
    }

    setError('');
    setIsProcessing(true);
    setResult(null);

    try {
      let response: any;

      // Extract the ID part from resource_id
      // For uploads, use the full selectedResourceId as-is
      // For others (patent-XXX, publication-XXX), extract the part after the first hyphen
      let resourceId = '';
      if (selectedResourceId) {
        if (selectedResourceType === 'upload') {
          // For uploads, use the entire selectedResourceId
          resourceId = selectedResourceId;
        } else {
          // For patents/publications/applications, extract after the first hyphen
          // e.g., 'publication-20190190617' -> '20190190617'
          resourceId = selectedResourceId.split('-').slice(1).join('-');
        }
      }
      const isApplication = selectedResourceType === 'application';
      const normalizedDocumentIdForRun = normalizeResourceId(selectedResourceId);

      // Execute task based on task ID
      if (task.id === 'parts_list') {
        response = await taskApi.submitPartsListTask(resourceId, selectedResourceType, isApplication);
      } else if (task.id === 'spec_map') {
        response = await taskApi.submitSpecMapTask(resourceId, selectedResourceType, isApplication);
      } else if (task.id === 'claims_support') {
        response = await taskApi.submitClaimsSupportTask(
          resourceId,
          selectedResourceType,
          isApplication,
          useEntireDescription,
          useEntireClaims
        );
      } else if (task.id === 'claims_with_numerals') {
        response = await taskApi.submitClaimsWithNumeralsTask(resourceId, selectedResourceType, isApplication);
      } else if (task.id === 'ir_metadata') {
        response = await taskApi.submitIRMetadataTask(textInput);
      } else if (task.id === 'ir_innovation') {
        response = await taskApi.submitIRInnovationTask(textInput);
      } else if (task.id === 'ir_tech_class') {
        response = await taskApi.submitIRTechClassTask(textInput);
      } else if (task.id === 'draft_summary') {
        response = await taskApi.submitDraftSummaryTask(textInput, resourceId || undefined);
      } else if (task.id === 'draft_detailed_description') {
        response = await taskApi.submitDraftDetailedDescriptionTask(textInput, undefined, resourceId || undefined);
      } else if (task.id === 'draft_background') {
        response = await taskApi.submitDraftBackgroundTask(textInput, resourceId || undefined);
      } else if (task.id === 'draft_claims') {
        response = await taskApi.submitDraftClaimsTask(textInput, resourceId || undefined);
      } else if (task.id === 'draft_other_embodiments') {
        response = await taskApi.submitDraftOtherEmbodimentsTask(textInput, resourceId || undefined);
      } else if (task.id === 'oa_extract') {
        response = await taskApi.submitOAExtractTask(selectedOfficeAction, selectedApplication);
      } else if (task.id === 'oa_analyze') {
        response = await taskApi.submitOAAnalyzeTask(selectedOfficeAction, selectedApplication);
      } else if (task.id === 'oa_summary_art') {
        response = await taskApi.submitOASummaryArtTask(selectedOfficeAction, selectedApplication);
      } else if (task.id === 'oa_reporting_email') {
        response = await taskApi.submitOAReportingEmailTask(selectedOfficeAction, selectedApplication, selectedEmailTemplate);
      } else if (task.id === 'oa_timeline') {
        response = await taskApi.submitOATimelineTask(selectedApplication);
      } else if (task.id === 'translate_document') {
        if (uploadedFile) {
          // Handle file upload translation
          response = await taskApi.submitTranslateDocumentFileTask(
            uploadedFile,
            sourceLanguage,
            targetLanguage,
            outputFilename || undefined,
            outputFormat as 'txt' | 'docx' | 'json' | undefined
          );
        } else {
          // Handle text or patent translation
          response = await taskApi.submitTranslateDocumentTask({
            text_content: textInput || undefined,
            resource_id: resourceId || undefined,
            resource_type: selectedResourceType || undefined,
            source_language: sourceLanguage,
            target_language: targetLanguage,
          });
        }
      } else if (task.id === 'latex_to_docx') {
        if (!uploadedFile) {
          setError('Please upload a LaTeX document (.tex or .zip file)');
          setIsProcessing(false);
          return;
        }
        response = await taskApi.submitLatexConversionTask(uploadedFile, outputFilename || undefined);
      } else if (task.id === 'ocr_pdf') {
        if (!uploadedFile) {
          setError('Please upload a PDF document');
          setIsProcessing(false);
          return;
        }
        response = await taskApi.submitOcrPdfTask(uploadedFile, outputFilename || undefined, outputFormat);
      } else if (task.id === 'custom_analysis') {
        response = await taskApi.submitCustomAnalysisTask(
          resourceId,
          selectedResourceType,
          isApplication,
          textInput
        );
      } else if (task.id === 'antecedent_basis_check') {
        // Validate input mode is selected
        if (!antecedentInputMode) {
          setError('Please select an input method');
          setIsProcessing(false);
          return;
        }

        // Handle different input types
        if (antecedentInputMode === 'onboarded') {
          if (!selectedResourceId) {
            setError('Please select a document');
            setIsProcessing(false);
            return;
          }
          // For patents and publications, extract the patent number
          if (selectedResourceType === 'patent' || selectedResourceType === 'publication') {
            response = await taskApi.submitAntecedentBasisCheck(
              'patent_number',
              selectedResourceId,
              '',  // custom_instruction
              outputFormat === 'json' ? 'json' : 'txt'
            );
          } else {
            // For uploads and other types, show error since backend needs file upload support for this
            setError('For uploaded documents, please use the "Upload a Document" option instead.');
            setIsProcessing(false);
            return;
          }
        } else if (antecedentInputMode === 'raw_text') {
          if (!textInput.trim()) {
            setError('Please enter claims text');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitAntecedentBasisCheck(
            'raw_text',
            textInput,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        } else if (antecedentInputMode === 'upload') {
          if (!uploadedFile) {
            setError('Please upload a document');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitAntecedentBasisCheck(
            'upload',
            uploadedFile,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        }
      } else if (task.id === 'technical_outline') {
        // Validate input mode is selected
        if (!technicalOutlineInputMode) {
          setError('Please select an input method');
          setIsProcessing(false);
          return;
        }

        // Handle different input types
        if (technicalOutlineInputMode === 'raw_text') {
          if (!textInput.trim()) {
            setError('Please enter disclosure text');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitTechnicalOutline(
            'raw_text',
            textInput,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        } else if (technicalOutlineInputMode === 'upload') {
          if (!uploadedFile) {
            setError('Please upload a document');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitTechnicalOutline(
            'upload',
            uploadedFile,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        }
      } else if (task.id === 'parts_process_outline') {
        // Validate input mode is selected
        if (!partsProcessOutlineInputMode) {
          setError('Please select an input method');
          setIsProcessing(false);
          return;
        }

        // Handle different input types
        if (partsProcessOutlineInputMode === 'raw_text') {
          if (!textInput.trim()) {
            setError('Please enter disclosure text');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitPartsProcessOutline(
            'raw_text',
            textInput,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        } else if (partsProcessOutlineInputMode === 'upload') {
          if (!uploadedFile) {
            setError('Please upload a document');
            setIsProcessing(false);
            return;
          }
          response = await taskApi.submitPartsProcessOutline(
            'upload',
            uploadedFile,
            '',  // custom_instruction
            outputFormat === 'json' ? 'json' : 'txt'
          );
        }
      } else {
        // For tasks not yet implemented
        setError('This task is not yet implemented in the API');
        setIsProcessing(false);
        return;
      }

      // Check if we got a job_id
      if (response && response.job_id) {
        console.log('Task started with job_id:', response.job_id);

        // Start polling for results
        taskApi.startGraduatedPolling(
          async () => {
            const result = await taskApi.getJobResult(response.job_id);
            console.log('Poll result:', result);

            // Check if we have a complete result (has analysis_id)
            if (result && (result as any).analysis_id) {
              return result;
            }

            // Return null to continue polling
            return null;
          },
          async (result) => {
            console.log('Task completed successfully:', result);
            // Extract timestamp from various possible locations
            const timestamp = result.time_received ||
                            result.timestamp ||
                            result.analysis?.timestamp ||
                            new Date().toISOString();

            // Debug: Log timestamp source
            console.log('Timestamp debugging:', {
              task_type: task.id,
              time_received: result.time_received,
              timestamp: result.timestamp,
              analysis_timestamp: result.analysis?.timestamp,
              using: timestamp
            });

            const normalizedResult: any = {
              ...result,
              task_id: task.id,
              task_name: task.name,
              timestamp: timestamp,
              output: result.analysis,
              credits_used: result.credits_used,
              job_id: result.analysis_id,
            };

            if (!getDocumentIdFromResult(normalizedResult) && normalizedDocumentIdForRun) {
              normalizedResult.document_id = normalizedDocumentIdForRun;
            }

            setResult(normalizedResult);
            setIsProcessing(false);

            // Show success notification
            addNotification({
              type: 'success',
              title: 'Task Complete',
              message: `${task.name} has finished processing and is ready to view.`,
              duration: 5000,
            });

            // Dispatch event to update credits
            window.dispatchEvent(new Event('credits-updated'));

            // Reload task history to show the new result
            loadTaskHistory();
          },
          (error) => {
            console.error('Task polling error:', error);
            setError(error.message || 'Failed to retrieve task results');
            setIsProcessing(false);

            // Show error notification
            addNotification({
              type: 'error',
              title: 'Task Failed',
              message: error.message || `${task.name} failed to complete. Please try again.`,
              duration: 8000,
            });
          },
          (attempts, elapsedTime) => {
            console.log(`Polling attempt ${attempts}, elapsed time: ${elapsedTime}ms`);
          }
        );
      } else {
        throw new Error('No job ID returned from server');
      }
    } catch (err: any) {
      console.error('Error running task:', error);
      setError(err.message || 'Failed to execute task');
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex-1 flex overflow-hidden bg-white h-full">
      {/* Left: Task Input Form */}
      <div className="w-1/2 border-r border-gray-200 flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              {CategoryIcon && (
                <div className="text-blue-500 text-xl mt-1">
                  <CategoryIcon className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">{task.name}</h2>
                  {task.coming_soon && (
                    <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
                      Coming Soon
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200 text-xs">
                    {categoryInfo.name}
                  </Badge>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="flex-shrink-0"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Task Input Form */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">{getTaskInstructions()}</p>
              </div>

              {/* Application Selection for Office Action Tasks */}
              {task.id.includes('oa_') && (
                <div>
                  <Label htmlFor="application-select" className="text-sm font-medium mb-2 block">
                    Select Application
                  </Label>
                  <Select value={selectedApplication} onValueChange={setSelectedApplication}>
                    <SelectTrigger id="application-select">
                      <SelectValue placeholder="Select an application..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {applicationsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading applications...
                        </SelectItem>
                      ) : sessionApplications.length === 0 ? (
                        <SelectItem value="none" disabled>
                          No applications available. Add applications first.
                        </SelectItem>
                      ) : (
                        <>
                          {/* Active applications section */}
                          {sessionApplications.filter(app => app.isActive).length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                Active Applications
                              </div>
                              {sessionApplications.filter(app => app.isActive).map((app) => (
                                <SelectItem key={app.application_id} value={app.application_id}>
                                  <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" />
                                    <span>{app.title || app.application_id}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </>
                          )}

                          {/* Other applications section */}
                          {sessionApplications.filter(app => !app.isActive).length > 0 && (
                            <>
                              {sessionApplications.filter(app => app.isActive).length > 0 && (
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                  Other Applications
                                </div>
                              )}
                              {sessionApplications.filter(app => !app.isActive).map((app) => (
                                <SelectItem key={app.application_id} value={app.application_id}>
                                  {app.title || app.application_id}
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select the application to analyze
                  </p>
                </div>
              )}

              {/* Office Action Document Selection (for tasks other than timeline) */}
              {requiresOfficeActionDocument() && selectedApplication && (
                <div>
                  <Label htmlFor="oa-document-select" className="text-sm font-medium mb-2 block">
                    Select Office Action
                  </Label>
                  <Select value={selectedOfficeAction} onValueChange={setSelectedOfficeAction}>
                    <SelectTrigger id="oa-document-select">
                      <SelectValue placeholder="Select an office action..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {prosecutionDocsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading office actions...
                        </SelectItem>
                      ) : prosecutionDocs.length === 0 ? (
                        <SelectItem value="none" disabled>
                          No office actions found for this application.
                        </SelectItem>
                      ) : (
                        prosecutionDocs.map((doc) => (
                          <SelectItem key={doc.docId} value={doc.docId}>
                            <div className="flex flex-col">
                              <span>{doc.description}</span>
                              <span className="text-xs text-gray-500">{doc.date}</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select the office action document to analyze
                  </p>
                </div>
              )}

              {/* Email Template Selection for OA Reporting Email */}
              {task.id === 'oa_reporting_email' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="email-template-select" className="text-sm font-medium">
                      Email Template
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTemplateManager(true)}
                      className="text-xs"
                    >
                      Manage Templates
                    </Button>
                  </div>
                  <Select value={selectedEmailTemplate} onValueChange={setSelectedEmailTemplate}>
                    <SelectTrigger id="email-template-select" className="truncate">
                      <SelectValue placeholder="Select an email template...">
                        {(() => {
                          // Find selected template and show only its name (truncated)
                          const customTemplate = userEmailTemplates.find((t: any) => t.prompt_id === selectedEmailTemplate);
                          if (customTemplate) {
                            return <span className="truncate">{customTemplate.name}</span>;
                          }
                          const defaultTemplate = OA_EMAIL_TEMPLATES.find(t => t.id === selectedEmailTemplate);
                          if (defaultTemplate) {
                            return <span className="truncate">{defaultTemplate.name}</span>;
                          }
                          return "Select an email template...";
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {/* User Custom Templates - shown first */}
                      {userEmailTemplates.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                            My Custom Templates
                          </div>
                          {userEmailTemplates.map((template: any) => (
                            <SelectItem key={template.prompt_id} value={template.prompt_id}>
                              <div className="flex flex-col">
                                <span className="font-medium">{template.name}</span>
                                <span className="text-xs text-gray-500">
                                  {template.description || 'Custom template'}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </>
                      )}

                      {/* Default Templates */}
                      <div className={`px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 ${userEmailTemplates.length > 0 ? 'border-t' : ''}`}>
                        Default Templates
                      </div>
                      {OA_EMAIL_TEMPLATES.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{template.name}</span>
                            <span className="text-xs text-gray-500">{template.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose the email format and tone
                  </p>
                </div>
              )}

              {/* Document Selection for Tasks that Require It */}
              {requiresDocumentSelection() && !task.id.includes('oa_') && (
                <div>
                  <Label htmlFor="document-select" className="text-sm font-medium mb-2 block">
                    Select Document
                  </Label>
                  <Select value={selectedResourceId} onValueChange={(value) => {
                    setSelectedResourceId(value);
                    // Find the selected document to determine its type
                    const selectedDoc = availableDocuments.find(
                      (doc) => doc.resource_id === value
                    );
                    if (selectedDoc) {
                      setSelectedResourceType(selectedDoc.file_type);
                    }
                  }}>
                    <SelectTrigger id="document-select">
                      <SelectValue placeholder={
                        isOfficeActionTask()
                          ? "Select an application..."
                          : task.id === 'custom_analysis'
                          ? "Select a document to analyze..."
                          : task.id.includes('draft_')
                          ? "Select a reference document (optional)..."
                          : "Select a patent or publication..."
                      } />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {documentsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading documents...
                        </SelectItem>
                      ) : availableDocuments.length === 0 ? (
                        <SelectItem value="none" disabled>
                          {isOfficeActionTask()
                            ? 'No application selected. Add applications first.'
                            : 'No documents available. Add documents first.'}
                        </SelectItem>
                      ) : (
                        <>
                          {/* Active documents section */}
                          {availableDocuments.filter(doc => doc.isActive).length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                Active Documents
                              </div>
                              {availableDocuments.filter(doc => doc.isActive).map((doc) => {
                                const docId = doc.resource_id;
                                const docName = doc.file_name || docId;
                                const docType = doc.file_type;

                                return (
                                  <SelectItem key={docId} value={docId}>
                                    <div className="flex items-center gap-2">
                                      <span className="text-blue-500" title="Active in current session">
                                        <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" />
                                      </span>
                                      <span>{docName}</span>
                                      <span className="text-xs text-gray-500">({docType})</span>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </>
                          )}

                          {/* Other documents section */}
                          {availableDocuments.filter(doc => !doc.isActive).length > 0 && (
                            <>
                              {availableDocuments.filter(doc => doc.isActive).length > 0 && (
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                  Other Documents
                                </div>
                              )}
                              {availableDocuments.filter(doc => !doc.isActive).map((doc) => {
                                const docId = doc.resource_id;
                                const docName = doc.file_name || docId;
                                const docType = doc.file_type;

                                return (
                                  <SelectItem key={docId} value={docId}>
                                    <div className="flex items-center gap-2">
                                      <span>{docName}</span>
                                      <span className="text-xs text-gray-500">({docType})</span>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </>
                          )}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {availableDocuments.length === 0
                      ? (isOfficeActionTask()
                          ? 'Add applications from the Examination page first'
                          : task.id === 'custom_analysis'
                          ? 'Add patents, publications, or uploads from the Documents page first'
                          : task.id.includes('draft_')
                          ? 'Add reference documents from the Documents page (optional - can draft from text alone)'
                          : 'Add patents or publications from the Documents page first')
                      : (isOfficeActionTask()
                          ? 'Select an application to analyze.'
                          : task.id === 'custom_analysis'
                          ? 'Select a document for your custom analysis.'
                          : task.id.includes('draft_')
                          ? 'Optionally select a reference document to inform the drafting.'
                          : 'Select a patent or publication to analyze.')}
                  </p>
                </div>
              )}

              {/* Claims Support Options */}
              {hasClaimsSupportOptions() && selectedResourceId && (
                <div className="space-y-2">
                  {task.resource_interface?.input?.use_entire_description && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="use-entire-description"
                        checked={useEntireDescription}
                        onCheckedChange={(checked) => setUseEntireDescription(checked as boolean)}
                      />
                      <Label
                        htmlFor="use-entire-description"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Use entire description
                      </Label>
                    </div>
                  )}
                  {task.resource_interface?.input?.use_entire_claims && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="use-entire-claims"
                        checked={useEntireClaims}
                        onCheckedChange={(checked) => setUseEntireClaims(checked as boolean)}
                      />
                      <Label
                        htmlFor="use-entire-claims"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Use entire claims
                      </Label>
                    </div>
                  )}
                </div>
              )}

              {/* Translation Task Input Mode Selection */}
              {task.id === 'translate_document' && !translationInputMode && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setTranslationInputMode('onboarded')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <FileText className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Select an Onboarded Document</div>
                        <div className="text-xs text-gray-500 mt-1">Choose from patents, publications, or uploads</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setTranslationInputMode('upload')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <Upload className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Upload a Document</div>
                        <div className="text-xs text-gray-500 mt-1">Upload PDF, DOCX, or TXT file</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setTranslationInputMode('text')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <MessageSquarePlus className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter Raw Text</div>
                        <div className="text-xs text-gray-500 mt-1">Type or paste text to translate</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Document Selection for Translation (Onboarded Mode) */}
              {task.id === 'translate_document' && translationInputMode === 'onboarded' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="translation-doc-select" className="text-sm font-medium">
                      Select Document to Translate
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTranslationInputMode(null);
                        setSelectedResourceId('');
                        setSelectedResourceType('');
                        setDocumentSearchQuery('');
                        setSearchResults([]);
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>

                  {/* Search Input */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          type="text"
                          placeholder="Search documents by name, ID, or title... (press Enter)"
                          value={documentSearchQuery}
                          onChange={(e) => setDocumentSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              // Search is already triggered by useEffect with debounce
                            }
                          }}
                          className="pl-10 pr-10"
                          disabled={isSearching}
                        />
                        {documentSearchQuery && !isSearching && (
                          <button
                            onClick={() => {
                              setDocumentSearchQuery('');
                              setSearchResults([]);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 pointer-events-auto"
                            type="button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          // Trigger immediate search
                          if (documentSearchQuery.trim().length >= 2) {
                            setDocumentSearchQuery(documentSearchQuery + ' '); // Force re-trigger
                            setTimeout(() => setDocumentSearchQuery(documentSearchQuery.trim()), 0);
                          }
                        }}
                        disabled={isSearching || documentSearchQuery.trim().length < 2}
                        className="px-4"
                      >
                        {isSearching ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>

                    {/* Search Results Info */}
                    {documentSearchQuery.trim().length >= 2 && !isSearching && (
                      <div className="text-xs text-gray-600">
                        {searchResults.length > 0 ? (
                          <span className="text-green-700">
                            ✓ Found {searchResults.length} match{searchResults.length !== 1 ? 'es' : ''}
                          </span>
                        ) : (
                          <span className="text-amber-700">No documents found matching "{documentSearchQuery}"</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Document Selector */}
                  <Select value={selectedResourceId} onValueChange={(value) => {
                    setSelectedResourceId(value);
                    const docsToSearch = documentSearchQuery ? searchResults : availableDocuments;
                    const selectedDoc = docsToSearch.find(
                      (doc) => doc.resource_id === value
                    );
                    if (selectedDoc) {
                      setSelectedResourceType(selectedDoc.file_type);
                    }
                  }}>
                    <SelectTrigger id="translation-doc-select">
                      <SelectValue placeholder="Select a document to translate..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {documentsLoading ? (
                        <SelectItem value="loading" disabled>
                          Loading documents...
                        </SelectItem>
                      ) : documentSearchQuery && searchResults.length === 0 && !isSearching ? (
                        <SelectItem value="none" disabled>
                          No documents found matching "{documentSearchQuery}"
                        </SelectItem>
                      ) : documentSearchQuery ? (
                        /* Show search results */
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                            Search Results ({searchResults.length})
                          </div>
                          {searchResults.map((doc) => {
                            const docId = doc.resource_id;
                            const docName = doc.file_name || docId;
                            const docType = doc.file_type;
                            const isActive = doc.is_active || (doc.session_ids && doc.session_ids.length > 0);

                            return (
                              <SelectItem key={docId} value={docId}>
                                <div className="flex items-center gap-2">
                                  {isActive && (
                                    <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" title="Active in current session" />
                                  )}
                                  <span>{docName}</span>
                                  <span className="text-xs text-gray-500">({docType})</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </>
                      ) : availableDocuments.length === 0 ? (
                        <SelectItem value="none" disabled>
                          No documents available. Add documents first.
                        </SelectItem>
                      ) : (
                        <>
                          {/* Active documents section */}
                          {availableDocuments.filter(doc => doc.isActive).length > 0 && (
                            <>
                              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                Active Documents
                              </div>
                              {availableDocuments.filter(doc => doc.isActive).map((doc) => {
                                const docId = doc.resource_id;
                                const docName = doc.file_name || docId;
                                const docType = doc.file_type;

                                return (
                                  <SelectItem key={docId} value={docId}>
                                    <div className="flex items-center gap-2">
                                      <span className="text-blue-500" title="Active in current session">
                                        <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0" />
                                      </span>
                                      <span>{docName}</span>
                                      <span className="text-xs text-gray-500">({docType})</span>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </>
                          )}

                          {/* Other documents section */}
                          {availableDocuments.filter(doc => !doc.isActive).length > 0 && (
                            <>
                              {availableDocuments.filter(doc => doc.isActive).length > 0 && (
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                                  Other Documents
                                </div>
                              )}
                              {availableDocuments.filter(doc => !doc.isActive).map((doc) => {
                                const docId = doc.resource_id;
                                const docName = doc.file_name || docId;
                                const docType = doc.file_type;

                                return (
                                  <SelectItem key={docId} value={docId}>
                                    <div className="flex items-center gap-2">
                                      <span>{docName}</span>
                                      <span className="text-xs text-gray-500">({docType})</span>
                                    </div>
                                  </SelectItem>
                                );
                              })}
                            </>
                          )}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {availableDocuments.length === 0
                      ? 'Add patents, publications, or uploads from the Documents page first'
                      : 'Select a document from your onboarded collection.'}
                  </p>
                </div>
              )}

              {/* Translation Language Selection */}
              {isTranslationTask() && translationInputMode && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="source-language" className="text-sm font-medium mb-2 block">
                      Source Language
                    </Label>
                    <Select value={sourceLanguage} onValueChange={setSourceLanguage}>
                      <SelectTrigger id="source-language">
                        <SelectValue placeholder="Select source language" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.map(lang => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="target-language" className="text-sm font-medium mb-2 block">
                      Target Language
                    </Label>
                    <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                      <SelectTrigger id="target-language">
                        <SelectValue placeholder="Select target language" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.filter(l => l.value !== 'auto').map(lang => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* File Upload for Document Translation */}
              {task.id === 'translate_document' && translationInputMode === 'upload' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="translate-file" className="text-sm font-medium">
                        Upload Document to Translate
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setTranslationInputMode(null);
                          setUploadedFile(null);
                          const fileInput = document.getElementById('translate-file') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Change Input Method
                      </Button>
                    </div>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="translate-file"
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />

                      <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center bg-gray-200">
                          <Upload className="w-6 h-6 text-gray-600" />
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            {isDragOver ? 'Drop document here' : 'Drag and drop your document here'}
                          </p>
                          <p className="text-xs mt-1 text-gray-500">
                            or{' '}
                            <label
                              htmlFor="translate-file"
                              className="text-blue-500 hover:text-blue-600 cursor-pointer font-medium"
                            >
                              browse to upload
                            </label>
                          </p>
                        </div>

                        <div className="text-xs text-gray-400">
                          Supports PDF, DOCX, and TXT files
                        </div>
                      </div>
                    </div>

                    {/* Selected File Display */}
                    {uploadedFile && (
                      <div className="mt-3 flex items-center justify-between p-3 rounded-lg border bg-white border-gray-200">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <FileText className="flex-shrink-0 w-5 h-5 text-gray-500" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate text-gray-900">{uploadedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setUploadedFile(null);
                            const fileInput = document.getElementById('translate-file') as HTMLInputElement;
                            if (fileInput) fileInput.value = '';
                          }}
                          disabled={isProcessing}
                          className="flex-shrink-0 ml-3 p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Output Options (only shown when file is uploaded) */}
                  {uploadedFile && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="output-filename" className="text-sm font-medium mb-2 block">
                          Output Filename (Optional)
                        </Label>
                        <Input
                          id="output-filename"
                          value={outputFilename}
                          onChange={(e) => setOutputFilename(e.target.value)}
                          placeholder="e.g., translated_document.txt"
                          disabled={isProcessing}
                        />
                      </div>
                      <div>
                        <Label htmlFor="output-format" className="text-sm font-medium mb-2 block">
                          Output Format
                        </Label>
                        <Select value={outputFormat} onValueChange={setOutputFormat}>
                          <SelectTrigger id="output-format">
                            <SelectValue placeholder="Select format" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="txt">Text (.txt)</SelectItem>
                            <SelectItem value="docx">Word (.docx)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Text Input for Document Translation */}
              {task.id === 'translate_document' && translationInputMode === 'text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="translation-text" className="text-sm font-medium">
                      Text to Translate
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTranslationInputMode(null);
                        setTextInput('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Textarea
                    id="translation-text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Enter text to translate..."
                    className="min-h-[200px]"
                    disabled={isProcessing}
                  />
                </div>
              )}

              {/* Antecedent Basis Check Input Mode Selection */}
              {task.id === 'antecedent_basis_check' && !antecedentInputMode && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAntecedentInputMode('onboarded')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <FileText className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Select an Onboarded Document</div>
                        <div className="text-xs text-gray-500 mt-1">Choose from patents, publications, or uploads</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAntecedentInputMode('upload')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <Upload className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Upload a Document</div>
                        <div className="text-xs text-gray-500 mt-1">Upload PDF, DOCX, TXT, or DOC file</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setAntecedentInputMode('raw_text')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <MessageSquarePlus className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter Raw Claims Text</div>
                        <div className="text-xs text-gray-500 mt-1">Type or paste patent claims directly</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Document Selection for Antecedent Basis Check (Onboarded Mode) */}
              {task.id === 'antecedent_basis_check' && antecedentInputMode === 'onboarded' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="antecedent-doc-select" className="text-sm font-medium">
                      Select Document to Analyze
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAntecedentInputMode(null);
                        setSelectedResourceId('');
                        setSelectedResourceType('');
                        setDocumentSearchQuery('');
                        setSearchResults([]);
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>

                  {/* Search Input */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          type="text"
                          placeholder="Search documents by name, ID, or title... (press Enter)"
                          value={documentSearchQuery}
                          onChange={(e) => setDocumentSearchQuery(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                            }
                          }}
                          className="pl-10 pr-10"
                          disabled={isSearching}
                        />
                        {documentSearchQuery && !isSearching && (
                          <button
                            onClick={() => {
                              setDocumentSearchQuery('');
                              setSearchResults([]);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 pointer-events-auto"
                            type="button"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (documentSearchQuery.trim().length >= 2) {
                            setDocumentSearchQuery(documentSearchQuery + ' ');
                            setTimeout(() => setDocumentSearchQuery(documentSearchQuery.trim()), 0);
                          }
                        }}
                        disabled={isSearching || documentSearchQuery.trim().length < 2}
                        className="px-4"
                      >
                        {isSearching ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                    </div>

                    {/* Search Results Info */}
                    {documentSearchQuery.trim().length >= 2 && !isSearching && (
                      <div className="text-xs text-gray-600">
                        {searchResults.length > 0 ? (
                          <span className="text-green-700">
                            ✓ Found {searchResults.length} match{searchResults.length !== 1 ? 'es' : ''}
                          </span>
                        ) : (
                          <span className="text-amber-700">No documents found matching "{documentSearchQuery}"</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Document Selector */}
                  <Select value={selectedResourceId} onValueChange={(value) => {
                    setSelectedResourceId(value);
                    const docsToSearch = documentSearchQuery ? searchResults : availableDocuments;
                    const selectedDoc = docsToSearch.find(
                      (doc) => doc.resource_id === value
                    );
                    if (selectedDoc) {
                      setSelectedResourceType(selectedDoc.file_type);
                    }
                  }}>
                    <SelectTrigger id="antecedent-doc-select" disabled={documentsLoading || isProcessing}>
                      <SelectValue placeholder={
                        documentsLoading ? 'Loading documents...' :
                        availableDocuments.length === 0 ? 'No documents available' :
                        'Select a document'
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      <ScrollArea className="max-h-[300px]">
                        {(documentSearchQuery ? searchResults : availableDocuments).map((doc) => (
                          <SelectItem
                            key={doc.resource_id}
                            value={doc.resource_id}
                            className="flex items-center gap-2"
                          >
                            <div className="flex items-center gap-2 w-full">
                              <FileText className="w-4 h-4 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">
                                  {doc.file_name || doc.resource_id}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-2">
                                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                                    {doc.file_type}
                                  </Badge>
                                  {doc.isActive && (
                                    <Badge variant="secondary" className="text-[10px] px-1 py-0 bg-green-100 text-green-800">
                                      Active
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>

                  {selectedResourceId && (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <div className="text-sm text-blue-900">
                        <span className="font-medium">Selected: </span>
                        {availableDocuments.find(d => d.resource_id === selectedResourceId)?.file_name || selectedResourceId}
                        <span className="text-xs text-blue-700 ml-2">
                          ({selectedResourceType})
                        </span>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-gray-500">
                    Claims will be extracted from the selected document and analyzed for antecedent basis issues
                  </p>
                </div>
              )}

              {/* Raw Text Input for Antecedent Basis Check */}
              {task.id === 'antecedent_basis_check' && antecedentInputMode === 'raw_text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="antecedent-claims-text" className="text-sm font-medium">
                      Claims Text
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setAntecedentInputMode(null);
                        setTextInput('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Textarea
                    id="antecedent-claims-text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder="Enter patent claims text (e.g., '1. A system comprising...')..."
                    className="min-h-[200px]"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500">
                    Enter claims with claim numbers (e.g., "1. A device...", "2. The device of claim 1...")
                  </p>
                </div>
              )}

              {/* File Upload for Antecedent Basis Check */}
              {task.id === 'antecedent_basis_check' && antecedentInputMode === 'upload' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="antecedent-file" className="text-sm font-medium">
                        Upload Document with Claims
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setAntecedentInputMode(null);
                          setUploadedFile(null);
                          const fileInput = document.getElementById('antecedent-file') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Change Input Method
                      </Button>
                    </div>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="antecedent-file"
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />

                      <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center bg-gray-200">
                          <Upload className="w-6 h-6 text-gray-600" />
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            {isDragOver ? 'Drop document here' : 'Drag and drop your document here'}
                          </p>
                          <p className="text-xs mt-1 text-gray-500">
                            or click to browse
                          </p>
                        </div>

                        <p className="text-xs text-gray-400">
                          Supports PDF, DOCX, TXT, DOC
                        </p>

                        {uploadedFile && (
                          <div className="mt-3 p-3 bg-white border rounded flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium">{uploadedFile.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUploadedFile(null);
                                const fileInput = document.getElementById('antecedent-file') as HTMLInputElement;
                                if (fileInput) fileInput.value = '';
                              }}
                              className="ml-auto h-6 w-6 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Technical Outline - Input Mode Selection */}
              {task.id === 'technical_outline' && !technicalOutlineInputMode && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setTechnicalOutlineInputMode('upload')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <Upload className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Upload Disclosure Document</div>
                        <div className="text-xs text-gray-500 mt-1">Upload PDF, DOCX, TXT, or DOC file</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setTechnicalOutlineInputMode('raw_text')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <MessageSquarePlus className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter Disclosure Text</div>
                        <div className="text-xs text-gray-500 mt-1">Type or paste invention disclosure directly</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Technical Outline - Raw Text Input */}
              {task.id === 'technical_outline' && technicalOutlineInputMode === 'raw_text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="technical-outline-text" className="text-sm font-medium">
                      Invention Disclosure Text
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTechnicalOutlineInputMode(null);
                        setTextInput('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Textarea
                    id="technical-outline-text"
                    placeholder="Enter your invention disclosure text here. Include details about the technical field, problems addressed, innovative features, components, and how the invention works..."
                    value={textInput}
                    onChange={(e) => {
                      setTextInput(e.target.value);
                      setError('');
                    }}
                    className="min-h-[300px] font-mono text-sm"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500">
                    The more detailed your disclosure, the more comprehensive the outline will be.
                  </p>
                </div>
              )}

              {/* Technical Outline - Upload Input */}
              {task.id === 'technical_outline' && technicalOutlineInputMode === 'upload' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="technical-outline-file" className="text-sm font-medium">
                        Upload Disclosure Document
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setTechnicalOutlineInputMode(null);
                          setUploadedFile(null);
                          const fileInput = document.getElementById('technical-outline-file') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Change Input Method
                      </Button>
                    </div>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="technical-outline-file"
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />
                      <div className="flex flex-col items-center gap-3 pointer-events-none">
                        <div className="p-3 bg-white rounded-full border border-gray-200">
                          <Upload className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            Drop your file here or click to browse
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Drag and drop your disclosure document
                          </p>
                        </div>
                        <p className="text-xs text-gray-400">
                          Supports PDF, DOCX, TXT, DOC
                        </p>

                        {uploadedFile && (
                          <div className="mt-3 p-3 bg-white border rounded flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium">{uploadedFile.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUploadedFile(null);
                                const fileInput = document.getElementById('technical-outline-file') as HTMLInputElement;
                                if (fileInput) fileInput.value = '';
                              }}
                              className="ml-auto h-6 w-6 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Parts & Process Outline - Input Mode Selection */}
              {task.id === 'parts_process_outline' && !partsProcessOutlineInputMode && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Choose Input Method</Label>
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPartsProcessOutlineInputMode('upload')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <Upload className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Upload Disclosure Document</div>
                        <div className="text-xs text-gray-500 mt-1">Upload PDF, DOCX, TXT, or DOC file</div>
                      </div>
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setPartsProcessOutlineInputMode('raw_text')}
                      className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-blue-50 hover:border-blue-500"
                    >
                      <MessageSquarePlus className="w-6 h-6 text-blue-600" />
                      <div className="text-center">
                        <div className="font-medium">Enter Disclosure Text</div>
                        <div className="text-xs text-gray-500 mt-1">Type or paste invention disclosure directly</div>
                      </div>
                    </Button>
                  </div>
                </div>
              )}

              {/* Parts & Process Outline - Raw Text Input */}
              {task.id === 'parts_process_outline' && partsProcessOutlineInputMode === 'raw_text' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="parts-process-outline-text" className="text-sm font-medium">
                      Invention Disclosure Text
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setPartsProcessOutlineInputMode(null);
                        setTextInput('');
                      }}
                      className="h-6 text-xs text-gray-500 hover:text-gray-700"
                    >
                      Change Input Method
                    </Button>
                  </div>
                  <Textarea
                    id="parts-process-outline-text"
                    placeholder="Enter your invention disclosure text here. Include detailed descriptions of components, parts, subparts, their relationships, process steps, and how the system operates..."
                    value={textInput}
                    onChange={(e) => {
                      setTextInput(e.target.value);
                      setError('');
                    }}
                    className="min-h-[300px] font-mono text-sm"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500">
                    Focus on specific parts, their technical details, and relationships for best results.
                  </p>
                </div>
              )}

              {/* Parts & Process Outline - Upload Input */}
              {task.id === 'parts_process_outline' && partsProcessOutlineInputMode === 'upload' && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="parts-process-outline-file" className="text-sm font-medium">
                        Upload Disclosure Document
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setPartsProcessOutlineInputMode(null);
                          setUploadedFile(null);
                          const fileInput = document.getElementById('parts-process-outline-file') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="h-6 text-xs text-gray-500 hover:text-gray-700"
                      >
                        Change Input Method
                      </Button>
                    </div>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="parts-process-outline-file"
                        type="file"
                        accept=".pdf,.docx,.txt,.doc"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />
                      <div className="flex flex-col items-center gap-3 pointer-events-none">
                        <div className="p-3 bg-white rounded-full border border-gray-200">
                          <Upload className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            Drop your file here or click to browse
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Drag and drop your disclosure document
                          </p>
                        </div>
                        <p className="text-xs text-gray-400">
                          Supports PDF, DOCX, TXT, DOC
                        </p>

                        {uploadedFile && (
                          <div className="mt-3 p-3 bg-white border rounded flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium">{uploadedFile.name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setUploadedFile(null);
                                const fileInput = document.getElementById('parts-process-outline-file') as HTMLInputElement;
                                if (fileInput) fileInput.value = '';
                              }}
                              className="ml-auto h-6 w-6 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Patent Document Selection */}
              {requiresPatentSelection() && !requiresDocumentSelection() && (
                <div>
                  <Label htmlFor="patent-number" className="text-sm font-medium mb-2 block">
                    Patent Number (Optional)
                  </Label>
                  <Input
                    id="patent-number"
                    value={patentNumber}
                    onChange={(e) => setPatentNumber(e.target.value)}
                    placeholder="e.g., US10123456, US20210123456"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optionally provide a patent number for enhanced context
                  </p>
                </div>
              )}

              {/* Application Number for Office Action/Form tasks */}
              {requiresApplicationNumber() && (
                <div>
                  <Label htmlFor="application-number" className="text-sm font-medium mb-2 block">
                    Application Number (Optional)
                  </Label>
                  <Input
                    id="application-number"
                    value={applicationNumber}
                    onChange={(e) => setApplicationNumber(e.target.value)}
                    placeholder="e.g., 16/123,456"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optionally provide an application number for context
                  </p>
                </div>
              )}

              {/* File Upload for LaTeX Conversion */}
              {task.id === 'latex_to_docx' && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="latex-file" className="text-sm font-medium mb-2 block">
                      LaTeX Document
                    </Label>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="latex-file"
                        type="file"
                        accept=".tex,.zip,.bib,.sty,.cls,.bst"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />

                      <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center bg-gray-200">
                          <Upload className="w-6 h-6 text-gray-600" />
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            {isDragOver ? 'Drop LaTeX file here' : 'Drag and drop your LaTeX file here'}
                          </p>
                          <p className="text-xs mt-1 text-gray-500">
                            or{' '}
                            <label
                              htmlFor="latex-file"
                              className="text-blue-500 hover:text-blue-600 cursor-pointer font-medium"
                            >
                              browse to upload
                            </label>
                          </p>
                        </div>

                        <div className="text-xs text-gray-400">
                          Supports .tex, .zip, .bib, .sty, .cls, .bst files
                        </div>
                      </div>
                    </div>

                    {/* Selected File Display */}
                    {uploadedFile && (
                      <div className="mt-3 flex items-center justify-between p-3 rounded-lg border bg-white border-gray-200">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <FileText className="flex-shrink-0 w-5 h-5 text-gray-500" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate text-gray-900">{uploadedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setUploadedFile(null);
                            setError('');
                          }}
                          className="flex-shrink-0 p-1 rounded-full transition-colors text-gray-400 hover:text-red-500 hover:bg-red-50"
                          disabled={isProcessing}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-2">
                      <span className="text-amber-600">Note: Complex equations may include LaTeX source for verification</span>
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="output-filename" className="text-sm font-medium mb-2 block">
                      Output Filename (optional)
                    </Label>
                    <Input
                      id="output-filename"
                      value={outputFilename}
                      onChange={(e) => setOutputFilename(e.target.value)}
                      placeholder="e.g., my_document.docx"
                      disabled={isProcessing}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty to use default filename
                    </p>
                  </div>
                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}
                </div>
              )}

              {/* OCR PDF Upload */}
              {task.id === 'ocr_pdf' && (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="ocr-pdf-file" className="text-sm font-medium mb-2 block">
                      PDF Document
                    </Label>

                    {/* Drag and Drop Area */}
                    <div
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragOver(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          setUploadedFile(file);
                          setError('');
                        }
                      }}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                        isDragOver
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                      } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        id="ocr-pdf-file"
                        type="file"
                        accept=".pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadedFile(file);
                            setError('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={isProcessing}
                      />

                      <div className="space-y-3">
                        <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center bg-gray-200">
                          <Upload className="w-6 h-6 text-gray-600" />
                        </div>

                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            {isDragOver ? 'Drop PDF file here' : 'Drag and drop your PDF file here'}
                          </p>
                          <p className="text-xs mt-1 text-gray-500">
                            or{' '}
                            <label
                              htmlFor="ocr-pdf-file"
                              className="text-blue-500 hover:text-blue-600 cursor-pointer font-medium"
                            >
                              browse to upload
                            </label>
                          </p>
                        </div>

                        <div className="text-xs text-gray-400">
                          Supports PDF files only
                        </div>
                      </div>
                    </div>

                    {/* Selected File Display */}
                    {uploadedFile && (
                      <div className="mt-3 flex items-center justify-between p-3 rounded-lg border bg-white border-gray-200">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <FileText className="flex-shrink-0 w-5 h-5 text-gray-500" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium truncate text-gray-900">{uploadedFile.name}</p>
                            <p className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setUploadedFile(null);
                            setError('');
                          }}
                          className="flex-shrink-0 p-1 rounded-full transition-colors text-gray-400 hover:text-red-500 hover:bg-red-50"
                          disabled={isProcessing}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    <p className="text-xs text-gray-500 mt-2">
                      <span className="text-amber-600">Note: OCR processing may take longer for scanned documents with many pages</span>
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="output-format" className="text-sm font-medium mb-2 block">
                      Output Format
                    </Label>
                    <select
                      id="output-format"
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value as 'txt' | 'json' | 'docx')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      disabled={isProcessing}
                    >
                      <option value="txt">Text (.txt) - Plain text output</option>
                      <option value="docx">Word (.docx) - Formatted document</option>
                      <option value="json">JSON (.json) - Includes positional data</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose Word for formatted document or JSON for detailed bounding box information
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="ocr-output-filename" className="text-sm font-medium mb-2 block">
                      Output Filename (optional)
                    </Label>
                    <Input
                      id="ocr-output-filename"
                      value={outputFilename}
                      onChange={(e) => setOutputFilename(e.target.value)}
                      placeholder={`e.g., ocr_output.${outputFormat}`}
                      disabled={isProcessing}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty to use default filename
                    </p>
                  </div>
                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}
                </div>
              )}

              {/* Text Input */}
              {requiresTextInput() && task.id !== 'latex_to_docx' && task.id !== 'ocr_pdf' && task.id !== 'translate_document' && task.id !== 'antecedent_basis_check' && task.id !== 'technical_outline' && task.id !== 'parts_process_outline' && (
                <div>
                  <Label htmlFor="text-input" className="text-sm font-medium mb-2 block">
                    {task.id === 'custom_analysis' ? 'Instruction/Request' :
                     isTranslationTask() ? 'Text to Translate (or upload a file above)' :
                     requiresPatentSelection() ? 'Text Content (or select a patent above)' :
                     'Text Content / Instructions'}
                  </Label>
                  <Textarea
                    id="text-input"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    placeholder={getInputPlaceholder()}
                    className="min-h-[200px] font-mono text-sm"
                    disabled={isProcessing}
                  />
                  {error && (
                    <p className="text-sm text-red-600 mt-2">{error}</p>
                  )}
                </div>
              )}

              {/* Run Button */}
              <div>
                <Button
                  onClick={handleRunTask}
                  disabled={isProcessing || task.coming_soon}
                  className="w-full"
                  size="lg"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing Task...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Run Task
                    </>
                  )}
                </Button>
                {task.coming_soon && (
                  <p className="text-sm text-yellow-700 mt-2">
                    This task is coming soon and not yet available.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

      {/* Right: Results and Task History */}
      <div className="w-1/2 flex flex-col bg-gray-50">
        {/* Results/History Tabs */}
        <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveTab('result')}
              className={`text-sm font-medium pb-2 transition-colors ${
                activeTab === 'result'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Current Result
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`text-sm font-medium pb-2 transition-colors ${
                activeTab === 'history'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Task History{!historyLoading && ` (${taskHistory.length})`}
            </button>
          </div>
        </div>

        {/* Content Display */}
        <ScrollArea className="flex-1">
          <div className="p-6 overflow-y-auto max-h-[calc(100vh-110px)]">
            {activeTab === 'result' ? (
              // Current Result Tab
              result ? (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-lg text-gray-900">Task Result</h3>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Add the current result to chat attachments
                      handleAddToChat(
                        result,
                        task.name,
                        task.id,
                        result.timestamp
                      );
                    }}
                    className="gap-2 mr-2"
                  >
                    <MessageSquarePlus className="w-4 h-4" />
                    Add to Chat
                  </Button>

                  {/* Task Metadata */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Completed:</span>
                        <div className="text-gray-900 mt-1">{formatTimestamp(result.timestamp || result.time_received)}</div>
                      </div>
                      {result.credits_used !== undefined && (
                        <div>
                          <span className="font-medium text-gray-700">Credits Used:</span>
                          <div className="text-gray-900 mt-1">{result.credits_used}</div>
                        </div>
                      )}
                      {(result.selected_number || result.document_id) && (
                        <div className="col-span-2">
                          <span className="font-medium text-gray-700">
                            {result.resource_type === 'office_action' ? 'Application:' : 'Document:'}
                          </span>
                          <div className="text-gray-900 mt-1 font-mono text-xs break-all">
                            {getDocumentDisplayName(
                              result.selected_number || result.document_id,
                              result.resource_type
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Render appropriate component based on task type */}
                  {(() => {
                    const parsedData = getParsedResult(getResultData(result));
                    const commonProps = { result: parsedData, theme: 'light' as const, showHeader: true };

                    switch (task.id) {
                      case 'parts_list':
                        return <PartsListDisplay {...commonProps} showToggle={true} />;
                      case 'spec_map':
                        return <SpecMapDisplay {...commonProps} showToggle={true} />;
                      case 'claims_support':
                        return <ClaimsSupportDisplay {...commonProps} showToggle={true} />;
                      case 'claims_with_numerals':
                        return <ClaimsWithNumeralsDisplay {...commonProps} showToggle={true} />;
                      case 'custom_analysis':
                        return <CustomAnalysisDisplay {...commonProps} showToggle={true} />;
                      case 'ir_metadata':
                        return <IRMetadataDisplay {...commonProps} />;
                      case 'ir_innovation':
                        return <IRInnovationAnalysisDisplay {...commonProps} />;
                      case 'ir_tech_class':
                        return <IRTechClassificationDisplay {...commonProps} />;
                      case 'draft_summary':
                        return <DraftSummaryDisplay {...commonProps} />;
                      case 'draft_detailed_description':
                        return <DraftDetailedDescriptionDisplay {...commonProps} />;
                      case 'draft_background':
                        return <DraftBackgroundDisplay {...commonProps} />;
                      case 'draft_claims':
                        return <DraftClaimsDisplay {...commonProps} />;
                      case 'draft_other_embodiments':
                        return <OtherEmbodimentsDisplay {...commonProps} />;
                      case 'oa_extract':
                        return <OAExtractDisplay {...commonProps} />;
                      case 'oa_analyze':
                        return <OAAnalyzeDisplay {...commonProps} />;
                      case 'oa_summary_art':
                        return <OASummaryArtDisplay {...commonProps} />;
                      case 'oa_timeline':
                        return <OATimelineDisplay {...commonProps} />;
                      case 'translate_document':
                        return <TranslateDocumentDisplay {...commonProps} />;
                      case 'latex_to_docx':
                        return <LaTeXConvertDisplay {...commonProps} />;
                      case 'ocr_pdf':
                        return <OCRPdfDisplay {...commonProps} />;
                      case 'antecedent_basis_check':
                        return <AntecedentBasisDisplay {...commonProps} />;
                      case 'technical_outline':
                        return <TechnicalOutlineDisplay {...commonProps} />;
                      case 'parts_process_outline':
                        return <PartsProcessOutlineDisplay {...commonProps} />;
                      case 'oa_reporting_email':
                        return (
                          <div className="space-y-4">
                            {/* Email output */}
                            <div className="p-4 rounded-lg bg-white border border-gray-200">
                              <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold text-gray-900">Generated Email Report</h3>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      const emailText = typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2);
                                      navigator.clipboard.writeText(emailText);
                                    }}
                                    className="text-xs px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                  >
                                    Copy to Clipboard
                                  </button>
                                  <button
                                    onClick={() => {
                                      const emailText = typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2);
                                      // Extract subject from first line if it looks like a subject line
                                      const lines = emailText.split('\n');
                                      let subject = 'Office Action Report';
                                      let body = emailText;

                                      // Check if first line is a subject
                                      const firstLine = lines[0]?.trim();
                                      if (firstLine && (firstLine.toLowerCase().startsWith('subject:') || firstLine.toLowerCase().startsWith('re:'))) {
                                        subject = firstLine.replace(/^subject:\s*/i, '').replace(/^re:\s*/i, 'Re: ');
                                        body = lines.slice(1).join('\n').trim();
                                      }

                                      // Create mailto link - note: there are length limitations (~2000 chars for URL)
                                      const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                                      window.open(mailtoUrl, '_blank');
                                    }}
                                    className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                  >
                                    Open in Email
                                  </button>
                                </div>
                              </div>
                              <pre className="text-sm font-mono whitespace-pre-wrap text-gray-800 leading-relaxed">
                                {typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2)}
                              </pre>
                            </div>
                          </div>
                        );
                      default:
                        return (
                          <div className="p-4 rounded-lg bg-gray-50">
                            <pre className="text-xs overflow-auto whitespace-pre-wrap">
                              {JSON.stringify(parsedData, null, 2)}
                            </pre>
                          </div>
                        );
                    }
                  })()}
                </div>
              ) : isProcessing ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                  <p className="text-sm text-gray-600">Processing your task...</p>
                  <p className="text-xs text-gray-500 mt-1">This may take a few moments</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mb-4" />
                  <p className="text-sm text-gray-600">No result yet</p>
                  <p className="text-xs text-gray-500 mt-1 max-w-sm">
                    Fill in the task details and click "Run Task" to see results here
                  </p>
                </div>
              )
            ) : (
              // Task History Tab
              historyLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
                  <p className="text-sm text-gray-600">Loading history...</p>
                </div>
              ) : taskHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mb-4" />
                  <p className="text-sm text-gray-600">No history yet</p>
                  <p className="text-xs text-gray-500 mt-1 max-w-sm">
                    Run this task to start building your history
                  </p>
                </div>
              ) : selectedHistoryItem ? (
                // Display selected history item
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-lg text-gray-900">Task Result</h3>
                    </div>
                    <div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAddToChat(
                                                    selectedHistoryItem,
                                                    AVAILABLE_TASKS.find(t => t.id === selectedHistoryItem.task_type)?.name || 'Task Result',
                                                    selectedHistoryItem.task_type,
                                                    selectedHistoryItem.time_received || selectedHistoryItem.timestamp
                                                  )}
                        className="gap-2 mr-2"
                      >
                        <MessageSquarePlus className="w-4 h-4" />
                        Add to Chat
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedHistoryItem(null)}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Close
                      </Button>
                    </div>
                  </div>

                  {/* Task Metadata */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Completed:</span>
                        <div className="text-gray-900 mt-1">{formatTimestamp(selectedHistoryItem.time_received || selectedHistoryItem.timestamp)}</div>
                      </div>
                      {selectedHistoryItem.credits_used !== undefined && (
                        <div>
                          <span className="font-medium text-gray-700">Credits Used:</span>
                          <div className="text-gray-900 mt-1">{selectedHistoryItem.credits_used}</div>
                        </div>
                      )}
                      {(selectedHistoryItem.selected_number || selectedHistoryItem.document_id) && (
                        <div className="col-span-2">
                          <span className="font-medium text-gray-700">
                            {selectedHistoryItem.resource_type === 'office_action' ? 'Application:' : 'Document:'}
                          </span>
                          <div className="text-gray-900 mt-1 font-mono text-xs break-all">
                            {getDocumentDisplayName(
                              selectedHistoryItem.selected_number || selectedHistoryItem.document_id,
                              selectedHistoryItem.resource_type
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Render appropriate component based on task type */}
                  {(() => {
                    const parsedData = getParsedResult(getResultData(selectedHistoryItem));
                    const commonProps = { result: parsedData, theme: 'light' as const, showHeader: true };

                    switch (selectedHistoryItem.task_type) {
                      case 'parts_list':
                        return <PartsListDisplay {...commonProps} showToggle={true} />;
                      case 'spec_map':
                        return <SpecMapDisplay {...commonProps} showToggle={true} />;
                      case 'claims_support':
                        return <ClaimsSupportDisplay {...commonProps} showToggle={true} />;
                      case 'claims_with_numerals':
                        return <ClaimsWithNumeralsDisplay {...commonProps} showToggle={true} />;
                      case 'custom_analysis':
                        return <CustomAnalysisDisplay {...commonProps} showToggle={true} />;
                      case 'ir_metadata':
                        return <IRMetadataDisplay {...commonProps} />;
                      case 'ir_innovation':
                        return <IRInnovationAnalysisDisplay {...commonProps} />;
                      case 'ir_tech_class':
                        return <IRTechClassificationDisplay {...commonProps} />;
                      case 'draft_summary':
                        return <DraftSummaryDisplay {...commonProps} />;
                      case 'draft_detailed_description':
                        return <DraftDetailedDescriptionDisplay {...commonProps} />;
                      case 'draft_background':
                        return <DraftBackgroundDisplay {...commonProps} />;
                      case 'draft_claims':
                        return <DraftClaimsDisplay {...commonProps} />;
                      case 'draft_other_embodiments':
                        return <OtherEmbodimentsDisplay {...commonProps} />;
                      case 'oa_extract':
                        return <OAExtractDisplay {...commonProps} />;
                      case 'oa_analyze':
                        return <OAAnalyzeDisplay {...commonProps} />;
                      case 'oa_summary_art':
                        return <OASummaryArtDisplay {...commonProps} />;
                      case 'oa_timeline':
                        return <OATimelineDisplay {...commonProps} />;
                      case 'translate_document':
                        return <TranslateDocumentDisplay {...commonProps} />;
                      case 'latex_to_docx':
                        return <LaTeXConvertDisplay {...commonProps} />;
                      case 'ocr_pdf':
                        return <OCRPdfDisplay {...commonProps} />;
                      case 'antecedent_basis_check':
                        return <AntecedentBasisDisplay {...commonProps} />;
                      case 'technical_outline':
                        return <TechnicalOutlineDisplay {...commonProps} />;
                      case 'parts_process_outline':
                        return <PartsProcessOutlineDisplay {...commonProps} />;
                      case 'oa_reporting_email':
                        return (
                          <div className="space-y-4">
                            {/* Email output */}
                            <div className="p-4 rounded-lg bg-white border border-gray-200">
                              <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold text-gray-900">Generated Email Report</h3>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => {
                                      const emailText = typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2);
                                      navigator.clipboard.writeText(emailText);
                                    }}
                                    className="text-xs px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                  >
                                    Copy to Clipboard
                                  </button>
                                  <button
                                    onClick={() => {
                                      const emailText = typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2);
                                      // Extract subject from first line if it looks like a subject line
                                      const lines = emailText.split('\n');
                                      let subject = 'Office Action Report';
                                      let body = emailText;

                                      // Check if first line is a subject
                                      const firstLine = lines[0]?.trim();
                                      if (firstLine && (firstLine.toLowerCase().startsWith('subject:') || firstLine.toLowerCase().startsWith('re:'))) {
                                        subject = firstLine.replace(/^subject:\s*/i, '').replace(/^re:\s*/i, 'Re: ');
                                        body = lines.slice(1).join('\n').trim();
                                      }

                                      // Create mailto link - note: there are length limitations (~2000 chars for URL)
                                      const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                                      window.open(mailtoUrl, '_blank');
                                    }}
                                    className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                  >
                                    Open in Email
                                  </button>
                                </div>
                              </div>
                              <pre className="text-sm font-mono whitespace-pre-wrap text-gray-800 leading-relaxed">
                                {typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2)}
                              </pre>
                            </div>
                          </div>
                        );
                      default:
                        return (
                          <div className="p-4 rounded-lg bg-gray-50">
                            <pre className="text-xs overflow-auto whitespace-pre-wrap">
                              {JSON.stringify(parsedData, null, 2)}
                            </pre>
                          </div>
                        );
                    }
                  })()}

                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddToChat(
                        selectedHistoryItem,
                        task.name,
                        selectedHistoryItem.task_type,
                        selectedHistoryItem.time_received || selectedHistoryItem.timestamp
                      )}
                      className="gap-2"
                    >
                      <MessageSquarePlus className="w-4 h-4" />
                      Add to Chat
                    </Button>
                  </div>
                </div>
              ) : (
                // List of history items
                <div className="space-y-2">
                  {taskHistory.map((item, index) => (
                    <button
                      key={item.analysis_id || index}
                      onClick={() => setSelectedHistoryItem(item)}
                      className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {task.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatTimestamp(item.time_received || item.timestamp)}
                          </p>
                          {item.selected_number && (
                            <p className="text-xs text-gray-400 mt-1 truncate">
                              Document: {getDocumentDisplayName(item.selected_number, item.resource_type)}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <Badge variant="outline" className="text-xs">
                            {item.credits_used || 0} credits
                          </Badge>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Email Template Manager Dialog */}
      <Dialog open={showTemplateManager} onOpenChange={(open) => {
        setShowTemplateManager(open);
        if (!open) {
          // Reload templates when dialog closes
          loadUserTemplates();
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden p-0">
          <EmailTemplateManager />
        </DialogContent>
      </Dialog>
    </div>
  );
}
